import { createAsyncThunk } from '@reduxjs/toolkit';

import { setLoading } from 'src/components/common/loading/loadingSlice';
import {
	IAIModel,
	ICreateAIModelThunkArgs,
	IUpdateAIModelThunkArgs,
	IGetAIModelsThunkArgs,
	IGetAIModelThunkArgs,
	IDeleteAIModelThunkArgs,
} from 'src/features/ai_model/domain/types';
import { AIModelApi } from 'src/features/ai_model/infrastructure/api';
import { handleThunkError } from 'src/utils/handleThunkError';

export const AI_MODEL_LOADING_KEY = 'ai_model';

export const createAIModel = createAsyncThunk<
	IAIModel,
	ICreateAIModelThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'ai_model/createAIModel',
	async (
		{ request, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: AI_MODEL_LOADING_KEY,
					loading: true,
				}),
			);
			const response = await AIModelApi.createAIModel(request, siteSlug);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to create AI model');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: AI_MODEL_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const updateAIModel = createAsyncThunk<
	IAIModel,
	IUpdateAIModelThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'ai_model/updateAIModel',
	async (
		{ id, request, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: AI_MODEL_LOADING_KEY,
					loading: true,
				}),
			);
			const response = await AIModelApi.updateAIModel(
				id,
				request,
				siteSlug,
			);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to update AI model');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: AI_MODEL_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const getAIModels = createAsyncThunk<
	IPaginatedResponse<IAIModel>,
	IGetAIModelsThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'ai_model/getAIModels',
	async (
		{ siteSlug, params, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: AI_MODEL_LOADING_KEY,
					loading: true,
				}),
			);
			const response = await AIModelApi.getAIModels(params, siteSlug);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to fetch AI models');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: AI_MODEL_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const getAIModel = createAsyncThunk<
	IAIModel,
	IGetAIModelThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'ai_model/getAIModel',
	async (
		{ aiModelId, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: AI_MODEL_LOADING_KEY,
					loading: true,
				}),
			);
			const response = await AIModelApi.getAIModel(aiModelId, siteSlug);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to fetch AI model');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: AI_MODEL_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const deleteAIModel = createAsyncThunk<
	void,
	IDeleteAIModelThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'ai_model/deleteAIModel',
	async (
		{ id, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: AI_MODEL_LOADING_KEY,
					loading: true,
				}),
			);
			await AIModelApi.deleteAIModel(id, siteSlug);

			onSuccess?.();
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to delete AI model');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: AI_MODEL_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);
